import { useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';

import { getCollectionDsp } from '@utils/api';
import { collectionDspToPlots } from '../utils/collectionDspToPlots';

/**
 * Fetches and shapes the collection's digital sampling plots for the current
 * project — the single-image counterpart to `useCollectionHexagons`.
 *
 * Data flow:
 *   persisted filters + visible objects → buildHexagonFilterBody (in the
 *   consumer) → this hook → getCollectionDsp (GeoJSON) → collectionDspToPlots →
 *   plots ready for the map layer / color filter.
 *
 * The filter body is built by the same helper the hexagon path uses: both
 * endpoints take the identical per-object range map, with the same "keys present
 * = the set to aggregate" semantics. What DSP does *not* take is a resolution — a
 * plot's size is its image footprint — so unlike the hexagon query there is no
 * cell-size input in the key.
 *
 * Refetch triggers:
 *   - `filterBody` change — only when filters are *applied* (persisted), so
 *     dragging a slider doesn't refetch, and when object visibility toggles
 *   - a finished detection job — `collectionDsp` is in
 *     COLLECTION_RESULT_QUERY_KEYS, so invalidateCollectionResults refreshes it
 *
 * The request is gated on there being at least one visible object: an empty
 * `filterBody` would otherwise be read by the endpoint as "aggregate every
 * object", which is the opposite of "nothing is visible".
 *
 * Mode/access gating (single-image project + collection, and the DSP tier) is the
 * mount site's job — CollectionResults only renders the layer when it applies —
 * so this hook doesn't re-check it.
 *
 * @param {string} projectUuid    - current project UUID
 * @param {string} collectionUuid - selected collection UUID
 * @param {object} filterBody     - per-object range map from buildHexagonFilterBody
 * @returns {{ plots: import('../utils/collectionDspToPlots').DspPlot[],
 *   isLoading: boolean, isFetching: boolean, error: unknown }}
 */
const useCollectionDsp = (projectUuid, collectionUuid, filterBody) => {
	// At least one visible object → something to aggregate. Also guards the
	// "empty body means aggregate-all" caveat documented in buildHexagonFilterBody.
	const hasVisibleObjects = Object.keys(filterBody ?? {}).length > 0;

	const { data, isLoading, isFetching, error } = useQuery({
		// React Query hashes the key deterministically, so the filterBody object
		// is a stable dependency — identical filters won't trigger a refetch.
		queryKey: ['collectionDsp', projectUuid, collectionUuid, filterBody],
		queryFn: () => getCollectionDsp(projectUuid, collectionUuid, filterBody),
		enabled: !!projectUuid && !!collectionUuid && hasVisibleObjects,
		// Keep the previous plots on screen while a new filter/visibility
		// aggregation loads — avoids the layer flickering empty on every toggle.
		placeholderData: keepPreviousData,
		// Stable until a key input changes or a detection job invalidates it.
		staleTime: 10 * 60 * 1000,
		// The empty state (404) resolves to [] rather than throwing, and a 422
		// means the request itself was invalid — retrying neither would help.
		retry: false,
	});

	const plots = useMemo(
		// When nothing is visible the query is disabled; keepPreviousData would
		// otherwise retain the last plots, so clear them explicitly.
		() => (hasVisibleObjects ? collectionDspToPlots(data ?? []) : []),
		[hasVisibleObjects, data]
	);

	return { plots, isLoading, isFetching, error };
};

export default useCollectionDsp;
